import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { defaultP5FormData, type P5FormData } from "./p5Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

const TYPE_LABELS: Record<string, string> = {
    interview: "Interview",
    observation: "Observation",
    data_request: "Data request",
    questionnaire: "Questionnaire",
    data_analysis: "Data analysis",
    sampling: "Sampling",
};

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

export function LauraP5Workspace(props: Props) {
    const { t } = useTranslation();
    return (
        <LauraStageShell<P5FormData>
            audit={props.audit}
            stageId="p5"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP5FormData}
            title={t("laura", "p5Title")}
            description="Interview guides, data requests, checklists, and sampling plan"
            generateLabel={t("laura", "generateInstruments")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2">
                        {form.instruments.map((inst) => (
                            <Badge key={inst.code} variant="outline" asChild>
                                <a href={`#inst-${inst.code}`}>
                                    {TYPE_LABELS[inst.type] ?? inst.type} · {inst.code}
                                </a>
                            </Badge>
                        ))}
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                const n = form.instruments.length + 1;
                                setForm((p) => ({
                                    ...p,
                                    instruments: [
                                        ...p.instruments,
                                        {
                                            code: `PT-${String(n).padStart(2, "0")}`,
                                            name: "",
                                            type: "interview",
                                            target: "",
                                            linked_qst: [],
                                            linked_procedures: [],
                                            duration: "",
                                            opening_script: "",
                                            sections: [],
                                            checklist_items: [],
                                            document_items: [],
                                            content_md: "",
                                        },
                                    ],
                                }));
                            }}
                        >
                            + instrument
                        </Button>
                    </div>

                    <CoverageMap instruments={form.instruments} />

                    {form.instruments.map((inst, idx) => (
                        <Card key={inst.code} id={`inst-${inst.code}`}>
                            <CardHeader className="pb-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <CardTitle className="text-sm">
                                        {inst.code} — {inst.name || inst.type}
                                    </CardTitle>
                                    <Badge variant="secondary">
                                        {TYPE_LABELS[inst.type] ?? inst.type}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-3">
                                <Field>
                                    <FieldLabel>Name</FieldLabel>
                                    <Input
                                        value={inst.name}
                                        onChange={(e) => {
                                            const instruments = [...form.instruments];
                                            instruments[idx] = {
                                                ...inst,
                                                name: e.target.value,
                                            };
                                            setForm((p) => ({ ...p, instruments }));
                                        }}
                                    />
                                </Field>
                                {inst.sections.map((sec, si) => (
                                    <Card key={si} className="bg-muted/30">
                                        <CardHeader className="py-2">
                                            <CardTitle className="text-xs">
                                                {sec.name || `Section ${si + 1}`}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="flex flex-col gap-2">
                                            {sec.questions.map((q, qi) => (
                                                <div key={qi}>
                                                    <p className="text-xs">{q}</p>
                                                    <Textarea
                                                        rows={2}
                                                        className="mt-1 text-xs"
                                                        placeholder="Notes (fieldwork)"
                                                        value={sec.notes}
                                                        onChange={(e) => {
                                                            const instruments = [
                                                                ...form.instruments,
                                                            ];
                                                            const sections = [...inst.sections];
                                                            sections[si] = {
                                                                ...sec,
                                                                notes: e.target.value,
                                                            };
                                                            instruments[idx] = {
                                                                ...inst,
                                                                sections,
                                                            };
                                                            setForm((p) => ({
                                                                ...p,
                                                                instruments,
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))}
                            </CardContent>
                        </Card>
                    ))}

                    <Card className="border-l-4 border-l-primary">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Sampling plan</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <FieldGroup>
                                <Field>
                                    <FieldLabel>Method</FieldLabel>
                                    <Input
                                        value={form.sampling.method}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                sampling: {
                                                    ...p.sampling,
                                                    method: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel>Sample size</FieldLabel>
                                    <Input
                                        value={form.sampling.sample_size}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                sampling: {
                                                    ...p.sampling,
                                                    sample_size: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                                <Field>
                                    <FieldLabel>Limitations</FieldLabel>
                                    <Textarea
                                        rows={2}
                                        className="text-xs"
                                        value={form.sampling.limitations}
                                        onChange={(e) =>
                                            setForm((p) => ({
                                                ...p,
                                                sampling: {
                                                    ...p.sampling,
                                                    limitations: e.target.value,
                                                },
                                            }))
                                        }
                                    />
                                </Field>
                            </FieldGroup>
                        </CardContent>
                    </Card>
                </div>
            )}
        </LauraStageShell>
    );
}

function CoverageMap({ instruments }: { instruments: P5FormData["instruments"] }) {
    const qsts = [...new Set(instruments.flatMap((i) => i.linked_qst))].sort();
    if (qsts.length === 0) return null;
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">QST coverage map</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Instrument</TableHead>
                            {qsts.map((q) => (
                                <TableHead key={q}>{q}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {instruments.map((inst) => (
                            <TableRow key={inst.code}>
                                <TableCell className="font-mono">{inst.code}</TableCell>
                                {qsts.map((q) => (
                                    <TableCell
                                        key={q}
                                        className={
                                            inst.linked_qst.includes(q)
                                                ? "bg-primary/10 text-center font-medium"
                                                : "text-center"
                                        }
                                    >
                                        {inst.linked_qst.includes(q) ? "✓" : ""}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
